import React from "react";
import { useSelector } from "react-redux";
import {
  NMStatsTable,
  DonutChart,
  Col,
} from "@neumetric/blueprint-lib/components";
import { getMitigatorDashboardStatsData } from "../../../store";
import { ExportSection } from "../ExportSection";

const SimpleColumns = [
  { typeRepresentation: "TITLE", title: "", attribute: "label", width: "65%" },
  {
    title: "OVERALL",
    id: "Overall",
    attribute: "value",
    width: "35%",
    highlight: { header: true, cell: true },
    typeRepresentation: "TEXT",
    overallFlag: true,
    align: "center-right",
  },
];

const dashZero = (v) => {
  const n = Number(v) || 0;
  return n === 0 ? "-" : n;
};

const pct1 = (num, den) => {
  const n = Number(num) || 0;
  const d = Number(den) || 0;
  if (d <= 0) return "0.0";
  return ((n / d) * 100).toFixed(1);
};

const DECISION_KEYS_ACCEPT = new Set(["accept", "acceptable"]);

function buildDecisionBlocks(risks_by_decision, companyLabel = "Luciance") {
  let accept = 0;
  let treat = 0;

  Object.entries(risks_by_decision || {}).forEach(([k, v]) => {
    const key = (k || "").toString().trim().toLowerCase();
    const n = Number(v) || 0;

    if (DECISION_KEYS_ACCEPT.has(key)) accept += n;
    else treat += n;
  });

  const total = accept + treat;

  const tableRows =
    total > 0
      ? [
          { label: companyLabel, value: total, highlight: true },
          { label: "Accept", value: dashZero(accept) },
          { label: "Treat", value: dashZero(treat) },
        ]
      : [];

  const datasets = [
    {
      key: "complete",
      label: "Accept",
      value: accept,
      slotOrder: 0,
      legendOrder: 0,
    },
    {
      key: "urgent",
      label: "Treat",
      value: treat,
      slotOrder: 1,
      legendOrder: 1,
    },
  ];

  return { tableRows, datasets, total, acceptCount: accept };
}

const STATUS_ORDER = [
  { valueKey: "accepted", colorKey: "closed", label: "Accepted", border: true },
  { valueKey: "pending", colorKey: "pending", label: "Pending" },
  {
    valueKey: "terminated",
    colorKey: "complete",
    label: "Terminated",
    border: true,
  },
  {
    valueKey: "tolerated",
    colorKey: "emergency",
    label: "Tolerated",
    border: true,
  },
  {
    valueKey: "transferred",
    colorKey: "abandoned",
    label: "Transferred",
    border: true,
  },
  { valueKey: "treated", colorKey: "complete", label: "Treated" },
];

function buildStatusBlocks(risks_by_status, companyLabel = "Luciance") {
  const byKey = {};
  Object.entries(risks_by_status || {}).forEach(([k, v]) => {
    byKey[(k || "").toString().trim().toLowerCase()] = Number(v) || 0;
  });

  const accepted = byKey["accepted"] || 0;

  const total = STATUS_ORDER.reduce(
    (acc, s) => acc + (byKey[s.valueKey] || 0),
    0,
  );

  const tableRows =
    total > 0
      ? [
          { label: companyLabel, value: total, highlight: true },
          ...STATUS_ORDER.map((s) => ({
            label: s.label,
            value: dashZero(byKey[s.valueKey] || 0),
          })),
        ]
      : [];

  const datasets = STATUS_ORDER.map((s, idx) => ({
    key: s.colorKey,
    label: s.label,
    value: byKey[s.valueKey] || 0,
    slotOrder: idx,
    legendOrder: idx,
    border: s.border ?? false,
  }));

  return { tableRows, datasets, total, acceptedCount: accepted };
}

export const RiskByDecisionAndStatus = ({ user, mode }) => {
  const mitigatorDashboardData = useSelector(getMitigatorDashboardStatsData);

  const decisions = mitigatorDashboardData?.risks_by_decision || {};
  const statuses = mitigatorDashboardData?.risks_by_status || {};

  const companyLabel = user?.org?.brandname ?? "Luciance";

  const decisionBlock = React.useMemo(
    () => buildDecisionBlocks(decisions, companyLabel),
    [decisions, companyLabel],
  );

  const statusBlock = React.useMemo(
    () => buildStatusBlocks(statuses, companyLabel),
    [statuses, companyLabel],
  );
  const decisionScorePct = React.useMemo(
    () => pct1(decisionBlock.acceptCount, decisionBlock.total),
    [decisionBlock.acceptCount, decisionBlock.total],
  );

  const statusScorePct = React.useMemo(
    () => pct1(statusBlock.acceptedCount, statusBlock.total),
    [statusBlock.acceptedCount, statusBlock.total],
  );

  const csvSimpleColumnforDecision = React.useMemo(() => {
    if (!Array.isArray(SimpleColumns) || SimpleColumns.length === 0) return [];

    return SimpleColumns.map((col, idx) => {
      if (idx !== 0) return col;

      return {
        ...col,
        title: "Decision vs Number of Risks", // CSV header only
      };
    });
  }, [SimpleColumns]);
  const csvSimpleColumnforStatus = React.useMemo(() => {
    if (!Array.isArray(SimpleColumns) || SimpleColumns.length === 0) return [];

    return SimpleColumns.map((col, idx) => {
      if (idx !== 0) return col;

      return {
        ...col,
        title: "Status vs Number of Risks", // CSV header only
      };
    });
  }, [SimpleColumns]);
  return (
    <>
      {mode === "fusion" ? (
        <div className="pl-10">
          <ExportSection
            index={1}
            moduleKey={"Mitigator_Decision_Status_vs_Risk_Status"}
            exportType={"table"}
            exportData={{
              title: "Decision vs Number of Risks",
              columns: csvSimpleColumnforDecision,
              rows: decisionBlock.tableRows,
            }}
            style={{
              display: "inline-block",
              width: 350,
              flex: "0 0 auto",
            }}
          >
            <Col style={{ flex: "1 1 0" }}>
              <div style={{ width: "60%", minWidth: 350, height: 220 }}>
                <DonutChart
                  classes={{ root: "h-200-px mt-12" }}
                  title="Decision vs Number of Risks"
                  subtitle="Risks:"
                  totalCount={decisionBlock.total}
                  datasets={decisionBlock.datasets}
                  metric={{
                    label: "Score",
                    value: decisionScorePct,
                    type: "percent",
                  }}
                />
              </div>
            </Col>
          </ExportSection>
          <ExportSection
            index={2}
            moduleKey={"Mitigator_Decision_Status_vs_Risk_Status"}
            exportType={"table"}
            exportData={{
              title: "Status vs Number of Risks",
              columns: csvSimpleColumnforStatus,
              rows: statusBlock.tableRows,
            }}
            style={{
              display: "inline-block",
              width: 350,
              flex: "0 0 auto",
            }}
          >
            <Col style={{ flex: "1 1 0" }}>
              <div style={{ width: "60%", minWidth: 350, height: 220 }}>
                <DonutChart
                  classes={{ root: "h-200-px mt-12" }}
                  title="Status vs Number of Risks"
                  subtitle="Risks:"
                  totalCount={statusBlock.total}
                  datasets={statusBlock.datasets}
                  metric={{
                    label: "Score",
                    value: statusScorePct,
                    type: "percent",
                  }}
                />
              </div>
            </Col>
          </ExportSection>
        </div>
      ) : (
        <div className="d-flex gap-20 p20">
          {/* Decision vs risks */}
          <ExportSection
            index={1}
            moduleKey={"Mitigator_Decision_Status_vs_Risk_Status"}
            exportType={"table"}
            exportData={{
              title: "Decision vs Number of Risks",
              columns: csvSimpleColumnforDecision,
              rows: decisionBlock.tableRows,
            }}
          >
            <div className="d-flex gap-20" style={{ flex: "1 1 50%" }}>
              <div style={{ width: "40%", minWidth: 280 }}>
                <NMStatsTable
                  title="Risks"
                  subTitle="Decision vs Number of Risks"
                  columns={SimpleColumns}
                  dataSource={decisionBlock.tableRows}
                />
              </div>
              <Col style={{ flex: "1 1 0" }}>
                <div style={{ width: "60%", minWidth: 350, height: 250 }}>
                  <DonutChart
                    classes={{ root: "h-220-px mt-12" }}
                    title="Decision vs Number of Risks"
                    subtitle="Risks:"
                    totalCount={decisionBlock.total}
                    datasets={decisionBlock.datasets}
                    metric={{
                      label: "Score",
                      value: decisionScorePct,
                      type: "percent",
                    }}
                  />
                </div>
              </Col>
            </div>
          </ExportSection>

          {/* Status vs risks */}
          <ExportSection
            index={2}
            moduleKey={"Mitigator_Decision_Status_vs_Risk_Status"}
            exportType={"table"}
            exportData={{
              title: "Status vs Number of Risks",
              columns: csvSimpleColumnforStatus,
              rows: statusBlock.tableRows,
            }}
          >
            <div className="d-flex gap-20" style={{ flex: "1 1 50%" }}>
              <div style={{ width: "40%", minWidth: 280 }}>
                <NMStatsTable
                  title="Risks"
                  subTitle="Status vs Number of Risks"
                  columns={SimpleColumns}
                  dataSource={statusBlock.tableRows}
                />
              </div>

              <Col style={{ flex: "1 1 0" }}>
                <div style={{ width: "60%", minWidth: 350, height: 250 }}>
                  <DonutChart
                    classes={{ root: "h-220-px mt-12" }}
                    title="Status vs Number of Risks"
                    subtitle="Risks:"
                    totalCount={statusBlock.total}
                    datasets={statusBlock.datasets}
                    metric={{
                      label: "Score",
                      value: statusScorePct,
                      type: "percent",
                    }}
                  />
                </div>
              </Col>
            </div>
          </ExportSection>
        </div>
      )}
    </>
  );
};
